import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import PropTypes from "prop-types";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";

import DefaultApiService from "../../../../../../infrastructure/api/default";
import LendingOperationsApi from "../../../../../../infrastructure/api/lendingOperations";
import FileUploader from "../../../../../components/shared/uploader/FileUploader";
import {
  getEntityId,
  parseApiErrorMessage,
} from "../../../../../components/request/case-tabs/mehrsanOperationsUtils";
import { normalizeWorkflowApiList } from "./WorkflowActionsCard";

export const pickDocumentFields = (doc) => ({
  id: getEntityId(doc),
  title: doc?.title ?? doc?.Title ?? "مدرک",
  documentType: doc?.documentType ?? doc?.DocumentType ?? "—",
  status: doc?.status ?? doc?.Status ?? "—",
  submissionId: doc?.submissionId ?? doc?.SubmissionId ?? null,
  fileId: doc?.fileId ?? doc?.FileId ?? null,
  code: doc?.code ?? doc?.Code ?? "",
});

export const canApproveDocument = (doc) => {
  const fields = pickDocumentFields(doc);
  if (!fields.submissionId || Number(fields.submissionId) <= 0) return false;
  const status = String(fields.status || "").toUpperCase();
  return status !== "APPROVED";
};

export const canSubmitDocument = (doc) => {
  const fields = pickDocumentFields(doc);
  if (fields.submissionId && Number(fields.submissionId) > 0) {
    const status = String(fields.status || "").toUpperCase();
    if (status === "SUBMITTED" || status === "APPROVED") return false;
  }
  return true;
};

const WorkflowDocumentsCard = ({
  source,
  loanProcessId,
  canEdit = false,
  fileBaseUrl = "",
}) => {
  const [loading, setLoading] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [submitOpen, setSubmitOpen] = useState(false);
  const [activeDoc, setActiveDoc] = useState(null);
  const [attachmentUrl, setAttachmentUrl] = useState("");
  const [fileId, setFileId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [approvingId, setApprovingId] = useState(null);
  const capturedFileIdRef = useRef(null);

  const loadDocuments = useCallback(async () => {
    if (!loanProcessId || Number(loanProcessId) <= 0) {
      setDocuments([]);
      return;
    }
    setLoading(true);
    try {
      const res = await LendingOperationsApi.getWorkflowDocuments(
        source,
        loanProcessId
      );
      setDocuments(normalizeWorkflowApiList(res));
    } catch {
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  }, [loanProcessId, source]);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  // FileUploader only returns directory via onChange; capture FileId from upload response.
  useEffect(() => {
    if (!submitOpen) return undefined;
    const original = DefaultApiService.uploadFile;
    DefaultApiService.uploadFile = async (...args) => {
      const result = await original(...args);
      const row = Array.isArray(result?.data) ? result.data[0] : null;
      const id = row?.fileId ?? row?.FileId ?? null;
      if (id != null && Number(id) > 0) {
        capturedFileIdRef.current = Number(id);
        setFileId(Number(id));
      }
      return result;
    };
    return () => {
      DefaultApiService.uploadFile = original;
    };
  }, [submitOpen]);

  const openSubmitDialog = (doc) => {
    setActiveDoc(doc);
    setAttachmentUrl("");
    setFileId(null);
    capturedFileIdRef.current = null;
    setSubmitOpen(true);
  };

  const closeSubmitDialog = () => {
    setSubmitOpen(false);
    setActiveDoc(null);
    setAttachmentUrl("");
    setFileId(null);
    capturedFileIdRef.current = null;
  };

  const handleSubmit = async () => {
    const fields = pickDocumentFields(activeDoc);
    const resolvedFileId = fileId || capturedFileIdRef.current;
    if (!fields.id || !resolvedFileId || Number(resolvedFileId) <= 0) {
      toast.error("لطفاً فایل مدرک را آپلود کنید.");
      return;
    }

    setSubmitting(true);
    try {
      const res = await LendingOperationsApi.submitWorkflowDocument(
        source,
        loanProcessId,
        fields.id,
        {
          FileId: Number(resolvedFileId),
          MetadataJson: null,
        }
      );
      if (res?.isSuccess === false) {
        throw new Error(parseApiErrorMessage(res, "ثبت مدرک ناموفق بود."));
      }
      toast.success("مدرک ثبت شد.");
      setSubmitting(false);
      closeSubmitDialog();
      await loadDocuments();
    } catch (err) {
      toast.error(err?.message || "ثبت مدرک ناموفق بود.");
      setSubmitting(false);
    }
  };

  const handleApprove = async (doc) => {
    const fields = pickDocumentFields(doc);
    if (!canApproveDocument(doc) || !canEdit) return;

    setApprovingId(fields.submissionId);
    try {
      const res = await LendingOperationsApi.approveWorkflowDocument(
        source,
        fields.submissionId
      );
      if (res?.isSuccess === false) {
        throw new Error(parseApiErrorMessage(res, "تأیید مدرک ناموفق بود."));
      }
      toast.success("مدرک تأیید شد.");
      await loadDocuments();
    } catch (err) {
      toast.error(err?.message || "تأیید مدرک ناموفق بود.");
    } finally {
      setApprovingId(null);
    }
  };

  if (!loanProcessId || Number(loanProcessId) <= 0) {
    return null;
  }

  return (
    <>
      <Card variant="outlined">
        <CardContent>
          <Stack spacing={1.5}>
            <Stack direction="row" spacing={1} alignItems="center">
              <DescriptionOutlinedIcon color="primary" fontSize="small" />
              <Typography variant="subtitle1" fontWeight={700}>
                مدارک گردش کار
              </Typography>
              {loading ? <CircularProgress size={16} /> : null}
            </Stack>

            {!loading && documents.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                مدرکی برای مرحله جاری تعریف نشده است.
              </Typography>
            ) : null}

            {documents.map((doc) => {
              const fields = pickDocumentFields(doc);
              if (!fields.id) return null;
              return (
                <Box
                  key={fields.id}
                  sx={{
                    p: 1.5,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1.5,
                  }}
                >
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={1}
                    alignItems={{ sm: "center" }}
                    justifyContent="space-between"
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={700}>
                        {fields.title}
                      </Typography>
                      <Stack
                        direction="row"
                        spacing={1}
                        sx={{ mt: 0.75 }}
                        flexWrap="wrap"
                        useFlexGap
                      >
                        <Chip
                          size="small"
                          label={fields.documentType}
                          variant="outlined"
                        />
                        <Chip size="small" label={fields.status || "—"} />
                      </Stack>
                    </Box>
                    {canEdit ? (
                      <Stack direction="row" spacing={1}>
                        {canSubmitDocument(doc) ? (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => openSubmitDialog(doc)}
                          >
                            ارسال
                          </Button>
                        ) : null}
                        {canApproveDocument(doc) ? (
                          <Button
                            size="small"
                            variant="contained"
                            disabled={approvingId === fields.submissionId}
                            onClick={() => handleApprove(doc)}
                          >
                            {approvingId === fields.submissionId
                              ? "در حال تأیید..."
                              : "تأیید"}
                          </Button>
                        ) : null}
                      </Stack>
                    ) : null}
                  </Stack>
                </Box>
              );
            })}
          </Stack>
        </CardContent>
      </Card>

      <Dialog
        open={submitOpen}
        onClose={() => !submitting && closeSubmitDialog()}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {pickDocumentFields(activeDoc).title || "ارسال مدرک"}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <FileUploader
              moduleName="SLN"
              label="فایل مدرک"
              accept="image/*,application/pdf"
              maxFiles={1}
              baseUrl={fileBaseUrl}
              value={attachmentUrl}
              onChange={(url) => {
                setAttachmentUrl(url || "");
                if (!url) {
                  setFileId(null);
                  capturedFileIdRef.current = null;
                }
              }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeSubmitDialog} disabled={submitting}>
            انصراف
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={submitting || !(fileId || capturedFileIdRef.current)}
          >
            {submitting ? "در حال ثبت..." : "ثبت مدرک"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

WorkflowDocumentsCard.propTypes = {
  source: PropTypes.string.isRequired,
  loanProcessId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  canEdit: PropTypes.bool,
  fileBaseUrl: PropTypes.string,
};

export default WorkflowDocumentsCard;
